import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:4005/api';
  }
  return `${window.location.protocol}//${hostname.replace('demomod', 'apimod')}/api`;
};

const API_URL = getApiUrl();

interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
  mustChangePassword: boolean;
  accesoConfiguracion: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{success: boolean; message?: string}>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{success: boolean; message?: string}>;
  validatePassword: (password: string) => Promise<{success: boolean; message?: string}>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'billing-system-auth';
const TOKEN_STORAGE_KEY = 'billing-system-token';
const REFRESH_TOKEN_STORAGE_KEY = 'billing-system-refresh-token';

// Configurar axios para incluir el token en todas las peticiones
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para refresh automático de tokens
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!refreshToken) {
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken,
        });

        if (response.data.success) {
          const newToken = response.data.accessToken;
          localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          processQueue(null, newToken);

          return axios(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Token refresh failed - logout
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cargar usuario guardado al iniciar
  useEffect(() => {
    const savedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (savedAuth && token) {
      try {
        const parsedUser = JSON.parse(savedAuth);
        setUser(parsedUser);
      } catch (e) {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Refresh automático del token cada 10 minutos
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      await refreshToken();
    }, 10 * 60 * 1000); // 10 minutos

    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (username: string, password: string): Promise<{success: boolean; message?: string}> => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        username,
        password,
      });

      if (response.data.success) {
        const { user, accessToken, refreshToken } = response.data;

        // Guardar tokens y usuario
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);

        // Configurar axios con el nuevo token
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

        setUser(user);
        return { success: true };
      }

      return { success: false, message: response.data.message || 'Error de autenticación' };
    } catch (error: any) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Error al conectar con el servidor'
      };
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (!storedRefreshToken) {
        return false;
      }

      const response = await axios.post(`${API_URL}/auth/refresh`, {
        refreshToken: storedRefreshToken,
      });

      if (response.data.success) {
        const { accessToken } = response.data;
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Refresh token error:', error);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      const refreshTokenValue = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

      if (refreshTokenValue) {
        await axios.post(`${API_URL}/auth/logout`, {
          refreshToken: refreshTokenValue,
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Limpiar todo
      setUser(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string): Promise<{success: boolean; message?: string}> => {
      try {
        const response = await axios.post(`${API_URL}/auth/change-password`, {
          oldPassword,
          newPassword,
        });

        if (response.data.success) {
          // Después de cambiar la contraseña, todos los tokens son revocados
          // Hacer login nuevamente
          if (user) {
            await login(user.username, newPassword);
          }
          return { success: true };
        }

        return { success: false, message: response.data.message };
      } catch (error: any) {
        console.error('Change password error:', error);
        return {
          success: false,
          message: error.response?.data?.message || 'Error al cambiar contraseña'
        };
      }
    },
    [user, login]
  );

  const validatePassword = useCallback(
    async (password: string): Promise<{success: boolean; message?: string}> => {
      try {
        const response = await axios.post(`${API_URL}/auth/validate-password`, {
          password,
        });

        return { success: response.data.success, message: response.data.message };
      } catch (error: any) {
        console.error('Validate password error:', error);
        return {
          success: false,
          message: error.response?.data?.message || 'Error al validar contraseña'
        };
      }
    },
    []
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshToken,
        changePassword,
        validatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
